<template>
    <div
        class="col-span-2 border border-tile-gray bg-tile-gray p-2 2xl:col-span-3"
    >
        <div class="">
            {{ useLocaleValue(metriky[0], "NAZEV_UDAJE").split(" – ")[0] }}
        </div>
        <div class="flex gap-2">
            <apexchart
                class="pointer-events-none"
                type="pie"
                :options="options"
                :series="series[0]"
                height="130"
                width="120"
            />
            <div class="flex flex-col justify-center gap-px">
                <div
                    v-for="(metrika, index) in metriky"
                    :key="index"
                    class="group flex cursor-pointer items-start gap-2"
                    :class="{
                        'mt-1 border-t border-black-text pt-1':
                            metrika.NAZEV_UDAJE.includes('celkem'),
                    }"
                    @click="handleClick(metrika.NAZEV_UDAJE_DATA.split(',')[0])"
                >
                    <div
                        class="relative top-0.5 h-3 w-3 rounded-full"
                        :style="{ backgroundColor: colors[index] }"
                    />
                    <div
                        v-if="!metrika.NAZEV_UDAJE.includes('celkem')"
                        class="text-xs group-hover:underline"
                        :class="{
                            underline:
                                store.selectedMetrika?.NAZEV_UDAJE_DATA.includes(
                                    metrika.NAZEV_UDAJE_DATA.split(
                                        ','
                                    )[0] as string
                                ),
                        }"
                    >
                        <span class="uppercase">{{
                            useLocaleValue(metrika, "NAZEV_UDAJE").split(
                                " – "
                            )[1] ?? $t("klok.celkem")
                        }}</span
                        >{{
                            store.selectedLocality
                                ? `: ${(series[0] as number[])[index]?.toFixed(2)} % (${(series[1] as number[])[index]?.toFixed(3)} ha)`
                                : ""
                        }}
                    </div>
                    <div
                        v-else
                        class="text-xs group-hover:underline"
                        :class="{
                            underline:
                                store.selectedMetrika?.NAZEV_UDAJE_DATA.includes(
                                    metrika.NAZEV_UDAJE_DATA.split(
                                        ','
                                    )[0] as string
                                ),
                        }"
                    >
                        <span class="uppercase">{{
                            useLocaleValue(metrika, "NAZEV_UDAJE").split(
                                " – "
                            )[1] ?? $t("klok.celkem")
                        }}</span
                        >{{
                            !noData
                                ? `: ${store.selectedLocality?.[metrika.NAZEV_UDAJE_DATA.split(",")[0] as string]?.toFixed(2)} % (${store.selectedLocality?.[metrika.NAZEV_UDAJE_DATA.split(",")[1] as string]?.toFixed(3)} ha)`
                                : ": 0.00 % (0.000 ha)"
                        }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import type { ApexOptions } from "apexcharts"
const mainStore = useMainStore()
const store = useKlokStore()
const props = defineProps<{
    metriky: KlokMetrikaItem[]
}>()

const hasCelkem = props.metriky.some(m => m.NAZEV_UDAJE.includes("celkem"))
const noData = computed(() => {
    if (!store.selectedLocality) return true
    if (hasCelkem) {
        return props.metriky.every(m => {
            const attrNames = m.NAZEV_UDAJE_DATA.split(",")
            return attrNames.every(name => {
                return (
                    (store.selectedLocality as KlokLokalitaItem)[name] === null
                )
            })
        })
    } else {
        return props.metriky.every(m => {
            const attrNames = m.NAZEV_UDAJE_DATA.split(",")
            return attrNames.every(name => {
                return (
                    (store.selectedLocality as KlokLokalitaItem)[name] === null
                )
            })
        })
    }
})

const NO_DATA_COLOR = "#8A8A8B"
const COLOR_PALLETE_DEFAULT = [
    "#46266A",
    "#A8DAE7",
    "#0482C5",
    "#AFD39C",
    "#FFE906",
    "#26AA60",
    "#00ADCE",
    "#C4D553",
]
const COLOR_PALLETE_VYUZITI = [
    "#c9090c",
    "#ffd933",
    "#3c72b0",
    "#a9d266",
    "#afafaf",
]
const COLOR_PALLETE_UP = ["#46266A", "#A8DAE7", NO_DATA_COLOR, "#0482C5"]

const colors = computed(() => {
    if (props.metriky?.[0]?.NAZEV_UDAJE_DATA.includes("VYUZ")) {
        return COLOR_PALLETE_VYUZITI
    } else if (props.metriky?.[0]?.NAZEV_UDAJE_DATA.includes("UP")) {
        return COLOR_PALLETE_UP
    } else {
        return COLOR_PALLETE_DEFAULT
    }
    /* const metrikyCount = props.metriky.length
    if (hasCelkem) {
        return COLOR_PALLETE.slice(0, metrikyCount - 1).concat(NO_DATA_COLOR)
    } else {
        return COLOR_PALLETE.slice(0, metrikyCount)
    } */
})

const options: ApexOptions = {
    chart: {
        id: props.metriky[0]?.NAZEV_UDAJE.split(" – ")[0],
        selection: {
            enabled: false,
        },
    },
    labels: props.metriky.map(m => m.NAZEV_UDAJE.split(" – ")[1] ?? "celkem"),
    legend: {
        show: false,
    },
    colors: colors.value,
    dataLabels: {
        enabled: false,
    },
    tooltip: {
        enabled: false,
    },
    plotOptions: {
        pie: {
            expandOnClick: false,
        },
    },
    stroke: {
        show: false,
    },
    states: {
        hover: {
            filter: {
                type: "none",
            },
        },
    },
}

const series = computed(() => {
    if (!store.selectedLocality || props.metriky.length === 0) {
        const defaultValues = [44, 55, 41, 17, 15]
        return [defaultValues.slice(0, props.metriky.length)]
    } else {
        if (hasCelkem) {
            const celkemValue = 100
            let offsetValue = 0
            props.metriky.forEach(m => {
                if (!m.NAZEV_UDAJE.includes("celkem")) {
                    const attrNames = m.NAZEV_UDAJE_DATA.split(",")
                    if (attrNames[0]) {
                        offsetValue += (
                            store.selectedLocality as Record<
                                string,
                                number | string | null
                            >
                        )[attrNames[0]] as unknown as number
                    }
                }
            })
            const attrs = store.selectedLocality as Record<
                string,
                number | string | null
            >

            const values = props.metriky.map(m => {
                if (!m.NAZEV_UDAJE.includes("celkem")) {
                    const attrNames = m.NAZEV_UDAJE_DATA.split(",")
                    return attrNames.map(name => {
                        return (attrs[name] as number) ?? 0
                    })
                } else {
                    return [celkemValue - offsetValue]
                }
            })
            const transposed = (values[0] as number[]).map((_, colIndex) =>
                values.map(row => row[colIndex])
            )
            return transposed
        } else {
            const attrs = store.selectedLocality as Record<
                string,
                number | string | null
            >

            const values = props.metriky.map(m => {
                const attrNames = m.NAZEV_UDAJE_DATA.split(",")
                return attrNames.map(name => {
                    return (attrs[name] as number) ?? 0
                })
            })
            const transposed = (values[0] as number[]).map((_, colIndex) =>
                values.map(row => row[colIndex])
            )
            return transposed
        }
    }
})

function handleClick(attrName: string | undefined) {
    if (!attrName) return
    if (store.selectedMetrika?.NAZEV_UDAJE_DATA.includes(attrName)) {
        store.selectedMetrika = null
        return
    }
    const metrika = props.metriky.find(m =>
        m.NAZEV_UDAJE_DATA.includes(attrName)
    )
    if (!metrika) return
    store.selectedMetrika = metrika
    mainStore.mobileLeftPanelOpen = false
}
</script>

<style scoped></style>
